import { httpResource } from '@angular/common/http';
import {
	inject,
	Injectable,
	InjectionToken,
	linkedSignal,
	signal,
} from '@angular/core';

import { MAPBOX_ACCESS_TOKEN } from '@valk-nx/core/lib/core';

import type {
	IsoCountryCode,
	LocationSuggestion,
	LocationSuggestResponse,
} from './location-search.interface';

export const MAPBOX_GEOCODING_DEFAULT_URL =
	'https://api.mapbox.com/search/geocode/v6/forward';

export const MAPBOX_GEOCODING_URL = new InjectionToken<string>(
	'mapbox-geocoding-url',
	{
		factory: () => MAPBOX_GEOCODING_DEFAULT_URL,
	},
);

export const MINIMUM_QUERY_LENGTH = 3;
export const SUGGESTION_LIMIT = 5;
export const SUGGESTION_TYPES = 'country,place,street,postcode';

@Injectable({ providedIn: 'root' })
export class LocationSearchService {
	private readonly accessToken = inject(MAPBOX_ACCESS_TOKEN);
	private readonly geocodingUrl = inject(MAPBOX_GEOCODING_URL);

	readonly query = signal('');
	readonly language = signal('');
	// Empty searches worldwide
	readonly countries = signal<IsoCountryCode[]>([]);

	readonly suggestionsResource = httpResource<LocationSuggestResponse>(() => {
		const query = this.query();
		if (query.trim().length < MINIMUM_QUERY_LENGTH) {
			return undefined;
		}

		const language = this.language();
		const countries = this.countries();
		return {
			url: this.geocodingUrl,
			params: {
				q: query,
				access_token: this.accessToken,
				autocomplete: true,
				limit: SUGGESTION_LIMIT,
				types: SUGGESTION_TYPES,
				...(language ? { language } : {}),
				...(countries.length ? { country: countries.join(',') } : {}),
			},
		};
	});

	// hasValue guard: value() throws while the resource is in the error state.
	// The last results stay visible while a new request loads, so refining a
	// term never flashes an empty "no results" list.
	readonly suggestions = linkedSignal<
		LocationSuggestion[] | undefined,
		LocationSuggestion[]
	>({
		source: () =>
			this.suggestionsResource.hasValue()
				? this.suggestionsResource
						.value()
						.features.map((feature) => feature.properties)
				: undefined,
		computation: (current, previous) => current ?? previous?.value ?? [],
	}).asReadonly();
}
